import React, {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { computePosition, type PositionResult } from '../floating/computePosition';
import { InputClearButton } from '../Input/InputClearButton';

type Option = { label: string; value: string };

type SelectSize = 'default' | 'dense' | 'compact';

type Props = {
  options: readonly Option[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  style?: React.CSSProperties;
  /** Extra classes on the root (e.g. a `w-[…]` to size the trigger). */
  className?: string;
  /** Remove outer border so the select sits flush inside a composite field. */
  inset?: boolean;
  disabled?: boolean;
  /** Compact 26px / 11.5px variant for dense toolbars and filter menus. */
  dense?: boolean;
  /**
   * Explicit size, taking priority over `dense` when supplied. `'compact'`
   * sits between `dense` (26px, toolbar chips) and the default (~36px,
   * square corners): 30px, rounded like `dense`, for controls that need the
   * rounded-pill look of a toolbar filter but read important enough (e.g. a
   * page's own period/entity scoping controls) to not be sized like one.
   */
  size?: SelectSize;
  /** Accessible name for the trigger (the visible value alone may lack context). */
  ariaLabel?: string;
  /**
   * Opt-in: style the trigger as carrying a non-default value — a colored
   * border + tinted background, distinct from the default "All …"/"Anyone"
   * look. Callers using this as a FILTER (as opposed to a plain form field,
   * which always has some value and has no "default" to contrast against)
   * should pass `value !== <its own default sentinel>`. Without this, a
   * dropdown with an active filter applied was visually IDENTICAL to its
   * default state — same border, same background, only the label text
   * differed — so a user had no at-a-glance way to tell which of several
   * filters were currently narrowing a list (Asana 1218340002615501).
   */
  active?: boolean;
  /**
   * `id` for the trigger element. The trigger is a `<button>`, which is a
   * labelable element, so a sibling `<label htmlFor={id}>` associates with it
   * exactly as it did with the native `<select>` this component replaces.
   * Without this, migrating a labelled native select would silently drop the
   * label association and leave the control unnamed to a screen reader.
   */
  id?: string;
  /** Opt-in: render a search box at the top of the dropdown to filter options. */
  searchable?: boolean;
  /**
   * Placeholder AND accessible name (`aria-label`) for the search input
   * (only used when `searchable`). Translate at the call site (this
   * primitive doesn't call `useTranslation` itself) — same convention as
   * `noResultsLabel` below. No default value (nexa-1115 review,
   * 2026-09-20 — a hardcoded 'Search…'/'Search options' fallback here
   * would silently ship untranslated English to every locale for any
   * `searchable` caller that forgets to pass this): a caller that opts
   * into `searchable` must supply its own translated string, or the
   * search input renders with no placeholder/accessible name at all
   * rather than in English.
   */
  searchPlaceholder?: string;
  /**
   * Message shown when a `searchable` dropdown's filtered options are empty.
   * Translate at the call site (this primitive doesn't call `useTranslation`
   * itself) — same convention as `MultiSelect`'s `emptyHint`. No default value
   * (nexa-1115 review, 2026-09-17 — an untranslated English fallback here
   * would silently ship to every locale for any `searchable` caller that
   * forgets to pass this): a caller that opts into `searchable` must supply
   * its own translated string, or the row renders empty rather than in
   * English.
   */
  noResultsLabel?: string;
  /**
   * Hand the typed term to the caller, which then owns `options`.
   *
   * Providing it turns the LOCAL filter OFF: the menu renders `options`
   * verbatim. That is the point rather than a side effect - a caller fetching
   * matches server-side has already decided what matches, and re-filtering the
   * result against this component's own `toLowerCase().includes()` would answer
   * a different question than the server was asked. It would also hide any row
   * the server matched on a field the label does not show.
   *
   * The caller is responsible for debouncing and for keeping the current value
   * present in `options`, or the trigger has no label to display. Mirrors
   * `ui/MultiSelect`'s prop of the same name.
   */
  onSearchChange?: (query: string) => void;
  /**
   * Opt-in: let option labels wrap onto multiple lines instead of the
   * default single-line truncation with an ellipsis. Every existing caller's
   * labels are short enough that truncation never mattered; this is for the
   * rare list of genuinely long labels (e.g. Category & Collection's
   * push-back reasons) where the default silently cut the text off.
   */
  wrapOptions?: boolean;
  /**
   * Raise the portaled menu's width CAP above the default 300px (240px on
   * `dense`).
   *
   * The menu is `w-max`, so it already grows to its longest label and floors at
   * the trigger's own width (`minWidth: anchorWidth`) — the cap is the only
   * thing that truncates a label the trigger is wide enough for. Two hundred
   * and something pixels covers every short option list, and every consumer
   * predates this prop, so raising the default would silently widen ~70 menus.
   *
   * Reach for it when the labels are genuinely long and the cap is what cuts
   * them: Burdened Cost's Customer control lists QB hierarchy PATHS
   * (`Compass Group USA Division -> Bon Appetit`), which run past 40 characters
   * and ellipsised at 300px in a picker whose whole job is to show what is
   * being picked. Prefer this over `wrapOptions` when the list is scanned
   * rather than read — a wrapped row has variable height, which is harder to
   * scan and makes the scroller's row count unpredictable.
   *
   * No overhang risk: unlike `MultiSelect`'s absolutely-positioned menu, this
   * one is portaled and its left edge is clamped to the viewport
   * (`computePosition` plus the `maxLeft` clamp below), so a menu wider than
   * the space to the right of its trigger shifts left instead of running off
   * screen.
   */
  menuMaxWidth?: number;
};

// Background/border split out of the size variants below into their own
// "chrome" classes (`TRIGGER_CHROME_DEFAULT`/`TRIGGER_CHROME_ACTIVE`), chosen
// ONE XOR THE OTHER at render — never both at once. They used to be baked
// into each size string, which is exactly why an `active` variant couldn't
// simply be layered on top: two utilities setting the same
// `background-color` (the size string's `bg-[var(--color-bg-input)]` and an
// appended `bg-[var(--color-primary-light)]`) would fight over Tailwind's
// generated stylesheet order, which the JSX class array's own order does not
// control (documented precedent for this exact gotcha: `OPTION_TRUNCATE_CLASS`
// vs `OPTION_WRAP_CLASS` below).
const TRIGGER_CHROME_DEFAULT =
  'bg-[var(--color-bg-input)] border-[var(--color-border)] hover:border-[var(--color-border-strong)]';
const TRIGGER_CHROME_ACTIVE =
  'bg-[var(--color-primary-light)] border-[var(--color-primary)] hover:border-[var(--color-primary)]';

const TRIGGER_BASE =
  'inline-flex items-center gap-1.5 w-full px-3 py-[7px] text-sm cursor-pointer select-none ' +
  // Reset native <button> chrome — this repo disables Tailwind preflight, so
  // without `appearance-none` residual button rendering skews the padding
  // compared to <Input>.
  'appearance-none ' +
  'text-[var(--color-text-primary)] ' +
  // Square corners (no border radius) per the design.
  'border rounded-none ' +
  'transition-[border-color,box-shadow,background-color] duration-fast';

const TRIGGER_OPEN =
  'border-[var(--color-primary)] shadow-[0_0_0_2px_var(--color-primary-light)]';

// Compact trigger for `dense` — matches the app's 26px/11.5px toolbar controls.
const TRIGGER_DENSE =
  'inline-flex items-center gap-1 w-full h-[26px] px-2 text-[11.5px] cursor-pointer select-none ' +
  'text-[var(--color-text-primary)] ' +
  'border rounded-md ' +
  'transition-[border-color,box-shadow,background-color] duration-fast';

// `size="compact"` — rounded like `dense`, but 30px/12.5px: a step up from a
// toolbar chip for controls that carry real page-level weight (e.g. period/
// entity scoping) without going all the way to the default's ~36px.
const TRIGGER_COMPACT =
  'inline-flex items-center gap-1.5 w-full h-[30px] px-2.5 text-[12.5px] cursor-pointer select-none ' +
  'text-[var(--color-text-primary)] ' +
  'border rounded-md ' +
  'transition-[border-color,box-shadow,background-color] duration-fast';

/**
 * How each size sizes its dropdown against the trigger. `width` clamps the panel
 * to the trigger; `minWidth` lets it grow past it. Keyed by all three sizes so a
 * new one has to make the choice rather than inherit it.
 */
const DROPDOWN_WIDTH_BY_SIZE: Record<
  SelectSize,
  (anchorWidth: number) => { width?: number; minWidth?: number }
> = {
  default: (w) => ({ width: w || undefined }),
  compact: (w) => ({ width: w || undefined }),
  dense: (w) => ({ minWidth: w || undefined }),
};

const TRIGGER_CLASS_BY_SIZE: Record<SelectSize, string> = {
  default: TRIGGER_BASE,
  dense: TRIGGER_DENSE,
  compact: TRIGGER_COMPACT,
};

// Portaled to `document.body` so it is never clipped by a scrollable ancestor
// (e.g. a Modal body). `fixed` + computed coords sit it above any overflow;
// z-[2100] keeps it above the Modal backdrop (z-[2000]).
/*
  Default size: no width class — the panel takes the trigger's width, applied
  inline as `width: anchorWidth`. This matches `MultiSelect`, whose panel class
  is likewise width-free for the same reason.

  It used to be `w-max max-w-[300px]` with `minWidth: anchorWidth`: "at least
  the trigger, but grow to fit the longest option". On the Job Tracker's 200px
  trigger that rendered a ~305px panel — half again as wide as the control it
  belongs to, which reads as broken rather than roomy. Options truncate
  instead, which is what a Select is expected to do.

  NO vertical padding on the SCROLLER - it lives on `OPTIONS_PAD_CLASS` below.

  A `sticky top-0` header cannot cover its scroll container's own top padding:
  the sticky offset resolves against the content box, so the padding strip sits
  ABOVE it and scrolled options travel through the gap. With `py-1` that was a
  4px band of moving text above the search box on every searchable Select. The
  sibling `ui/MultiSelect` has no container padding for the same reason.

  Moving it onto the option list rather than deleting it keeps the menu's
  spacing identical in both cases - a non-searchable Select has no sticky header
  and never had the bug, so it must not lose 4px to the fix.
*/
const DROPDOWN_CLASS =
  'fixed z-[2100] list-none m-0 max-h-60 overflow-y-auto ' +
  'bg-[var(--color-bg-primary)] border border-[var(--color-border)] rounded-md shadow-md';
/*
  `dense` KEEPS grow-to-content, deliberately. Dense triggers are the narrow
  inline pills used by the SPI toolbars and table cells — `SpiFilterToolbar`
  caps its trigger at `maxWidth: 180`, `ColumnFilterMenu` and
  `DetailIncomingPos` sit inside a menu and a table cell. Those are sized to
  fit a row, not to fit their option text, so binding the panel to the trigger
  would truncate labels that are currently readable. The mismatch reported on
  the Job Tracker is a default-size problem; this variant is not it, and the
  two caps already differed (240 vs 300) before this change.
*/
const DROPDOWN_CLASS_DENSE =
  'fixed w-max max-w-[240px] z-[2100] list-none m-0 max-h-60 overflow-y-auto ' +
  'bg-[var(--color-bg-primary)] border border-[var(--color-border)] rounded-md shadow-md';
/** The scroller's former `py`, now on the list so the sticky header sits flush. */
const OPTIONS_PAD_CLASS = 'py-1';
const OPTIONS_PAD_CLASS_DENSE = 'py-0.5';

const OPTION_DENSE = 'px-2.5 py-[5px] text-[11.5px]';

// Sizing is split out so `dense` swaps it without conflicting utility classes.
// Colour/interaction classes only — the truncation classes are split into
// `OPTION_TRUNCATE_CLASS` below and applied conditionally. Appending a
// `whitespace-normal` override class alongside `whitespace-nowrap` does NOT
// win: Tailwind emits `.whitespace-normal` BEFORE `.whitespace-nowrap` in
// its generated stylesheet, so with equal specificity `nowrap` always wins
// regardless of class order in the JSX — the truncation classes have to be
// OMITTED, not overridden.
const OPTION_BASE =
  'text-[var(--color-text-primary)] cursor-pointer transition-colors duration-fast ' +
  'hover:bg-[var(--color-bg-subtle)]';
// Default: single-line, ellipsis-truncated — every existing consumer's
// option labels are short enough that this never mattered.
const OPTION_TRUNCATE_CLASS = 'whitespace-nowrap overflow-hidden text-ellipsis';
// `wrapOptions` opt-out (below): a caller with genuinely long labels (e.g.
// push-back reasons) gets this instead, letting the label wrap onto
// multiple lines rather than being cut off.
const OPTION_WRAP_CLASS = 'whitespace-normal';
const OPTION_SIZE = 'px-3 py-2 text-sm';

const OPTION_SELECTED =
  'bg-[var(--color-accent-light)] text-[var(--color-primary)] font-hk-medium hover:bg-[var(--color-accent-light)]';
// The keyboard-active (not necessarily selected) option's highlight — same
// fill as the existing hover state, so the visual language stays
// consistent whether the row is "active" via arrow keys or via the mouse
// (nexa-1115 review, 2026-09-20 — added alongside the listbox's roving
// aria-activedescendant management). Never combined with OPTION_SELECTED
// on the same row (see the if/else that picks one below) — two bg-*
// utilities at once is the exact Tailwind-emission-order gotcha already
// documented on TRIGGER_CHROME_DEFAULT/ACTIVE above.
const OPTION_ACTIVE = 'bg-[var(--color-bg-subtle)]';

// Sticky so it stays pinned while the option list scrolls beneath it; z-10
// keeps it painted above the option rows rather than blending into them.
// Flush with the panel edges and using the SAME horizontal padding as
// `OPTION_BASE` (px-3) so the search text lines up exactly with the option
// text below it — a boxed/inset input here previously read as a left gap.
const SEARCH_WRAP_CLASS =
  'sticky top-0 z-10 bg-[var(--color-bg-primary)] p-1.5 border-b border-[var(--color-border)]';

// A visibly bordered field (matches MultiSelect's search box) — the
// previous border-0/bg-transparent version rendered as bare placeholder
// text with no field chrome, unrecognizable as an input.
// `flex-1 min-w-0` rather than `w-full`: the input now shares its row with the
// clear button, and `w-full` would push the button past the field's own border.
const SEARCH_INPUT_CLASS =
  'flex-1 min-w-0 px-2.5 py-1.5 text-sm rounded-l border-0 bg-transparent ' +
  'text-[var(--color-text-primary)] outline-none';
// The field chrome moved OFF the input and onto this row, so the clear button
// sits INSIDE the border with the text rather than beside it. Focus is drawn
// with `focus-within`, which the input alone can no longer do for the pair.
const SEARCH_FIELD_CLASS =
  'flex items-center rounded border border-solid border-[var(--color-border)] ' +
  'bg-[var(--color-bg-input)] pr-1.5 ' +
  'focus-within:border-[var(--color-accent)]';

const NO_RESULTS_CLASS =
  'px-3 py-2 text-sm text-[var(--color-text-muted)] whitespace-nowrap';

/** Distance (px) between the trigger and the portaled dropdown panel. */
const DROPDOWN_OFFSET_PX = 4;

/**
 * The panel's natural height cap — matches `max-h-60` (15rem) on both
 * `DROPDOWN_CLASS`/`DROPDOWN_CLASS_DENSE`. Kept as its own constant because
 * `computeSelectPanelLayout()` below needs the SAME number in JS to compute
 * a whole-row-count height, not just a Tailwind class.
 */
const DROPDOWN_MAX_HEIGHT_PX = 240;

/**
 * The root wrapper's className — extracted, with the trigger's and
 * chevron's below, so their own branching doesn't count against `Select`'s
 * own Cognitive Complexity (SonarQube S3776). Each is a component-specific
 * className builder taking the actual named inputs it needs, NOT a generic
 * `cond ? value : ''` selector helper reused across unrelated call sites —
 * an earlier version used exactly that shape and was separately flagged
 * ("provide multiple methods instead of using a condition to determine
 * which action to take", 2026-09-20) for the boolean-flag-parameter smell
 * that pattern is.
 */
function selectRootClassName(inset: boolean, className: string | undefined): string {
  return ['relative inline-flex flex-col', inset ? 'h-full' : '', className]
    .filter(Boolean)
    .join(' ');
}

/** The trigger button's className — see `selectRootClassName`'s doc. */
function selectTriggerClassName(
  resolvedSize: SelectSize,
  triggerChromeClass: string,
  open: boolean,
  inset: boolean,
  disabled: boolean,
): string {
  return [
    TRIGGER_CLASS_BY_SIZE[resolvedSize],
    triggerChromeClass,
    open ? TRIGGER_OPEN : '',
    // `h-full` here (not just on the wrapper) is what actually lets the
    // trigger fill a fixed-height parent — the wrapper alone stretching to
    // `h-full` does nothing unless the button inside it also grows to
    // match (nexa-1115 review, 2026-09-16 — no current caller passes
    // `inset` yet, so this was unreachable dead code, not a live
    // regression).
    inset ? 'h-full border-0 bg-transparent rounded-none shadow-none' : '',
    disabled ? 'opacity-50 cursor-not-allowed pointer-events-none' : '',
  ]
    .filter(Boolean)
    .join(' ');
}

/** The trigger's chevron icon className — see `selectRootClassName`'s doc. */
function selectChevronClassName(open: boolean): string {
  return [
    'shrink-0 text-[var(--color-text-muted)] transition-transform duration-fast',
    open ? 'rotate-180' : '',
  ]
    .filter(Boolean)
    .join(' ');
}

type MaxPanelHeight = number | undefined;

interface SelectPanelLayout {
  pos: PositionResult;
  maxPanelHeight: MaxPanelHeight;
}

/**
 * One measurement pass for the portaled panel's position + row-aligned
 * max-height — extracted out of the positioning `useLayoutEffect` (SonarQube:
 * Select's own Cognitive Complexity, 2026-09-20). Pure given the DOM rect/
 * computed style it reads; identical behavior to the inline version it
 * replaces.
 */
function computeSelectPanelLayout(anchorRect: DOMRect, panel: HTMLElement): SelectPanelLayout {
  const contentRect = panel.getBoundingClientRect();
  const vertical = computePosition({
    anchor: anchorRect,
    content: contentRect,
    placement: 'bottom',
    offset: DROPDOWN_OFFSET_PX,
  });
  // `computePosition` centers the floater under the anchor (correct for a
  // Popover/Tooltip). A Select's dropdown must instead LEFT-align with its
  // trigger — when a caller sizes the trigger to fill its container's full
  // width and the panel shrinks to its own content width, centering shifts
  // it far right of the trigger's left edge. Keep computePosition's vertical
  // flip/top-vs-bottom decision, but override `left` to align with the
  // anchor, clamped so it never overflows the viewport.
  const viewportPadding = 6;
  const maxLeft = window.innerWidth - contentRect.width - viewportPadding;
  const left = Math.max(viewportPadding, Math.min(anchorRect.left, maxLeft));
  const pos: PositionResult = { top: vertical.top, left, placement: vertical.placement };

  // Round the max-height down to a whole number of option rows so the
  // bottom-most visible row is never sliced mid-line (reported: a
  // partially-visible cropped row at the bottom of the list reads as "text
  // is hidden/cut off", even though the row itself is fully reachable by
  // scrolling one row further). `max-h-60`'s flat 240px isn't a multiple of
  // any option row's rendered height, so it always stopped partway through
  // whichever row happened to land on the boundary. Measuring the actual
  // rendered row (rather than hardcoding a row-height constant that mirrors
  // `OPTION_SIZE`/`OPTION_DENSE`) means this stays correct if those ever
  // change, and works regardless of the browser's font metrics or the
  // user's zoom level.
  // Scoped to `role="option"` (nexa-1115 review, 2026-09-20) — a plain
  // `button[type="button"]` selector also matches `InputClearButton` inside
  // the search field, which precedes the options in document order and is
  // itself a `type="button"` element. Once a query is typed, a recompute
  // (e.g. the window resize/scroll listeners below) would measure the
  // clear button's height instead of an option row's, silently
  // reintroducing the "bottom row sliced mid-line" bug this function
  // exists to fix.
  const firstOption = panel.querySelector('[role="option"]');
  const rowHeight = firstOption?.getBoundingClientRect().height ?? 0;
  if (rowHeight === 0) {
    return { pos, maxPanelHeight: undefined };
  }

  const searchBox = panel.querySelector('[data-select-search]');
  const searchHeight = searchBox ? searchBox.getBoundingClientRect().height : 0;
  // What's visible at scrollTop 0 (before any row is scrolled past) is
  // bounded by the padding-box: border eats into the box on BOTH edges, but
  // padding only matters on the TOP — content starts right after
  // padding-top, so that's what has to be budgeted before the rows;
  // padding-BOTTOM is only ever seen once scrolled all the way to the true
  // end, so it plays no part in how many whole rows fit in the initial view
  // (verified live — including it here left the very next row peeking a
  // few px into view instead of being fully clipped).
  // `parseFloat` on an unset/empty computed-style value (e.g. jsdom in
  // tests, which resolves no real stylesheet) yields `NaN`, not `0` —
  // guarded per value so a missing style never poisons the whole sum.
  const panelStyle = window.getComputedStyle(panel);
  const px = (value: string): number => parseFloat(value) || 0;
  const borderY = px(panelStyle.borderTopWidth) + px(panelStyle.borderBottomWidth);
  const paddingTop = px(panelStyle.paddingTop);
  const reservedBeforeRows = borderY + paddingTop + searchHeight;
  const availableForRows = DROPDOWN_MAX_HEIGHT_PX - reservedBeforeRows;
  const rows = Math.max(1, Math.floor(availableForRows / rowHeight));
  return { pos, maxPanelHeight: reservedBeforeRows + rows * rowHeight };
}

export function Select({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  style,
  className,
  inset = false,
  disabled = false,
  dense = false,
  size,
  ariaLabel,
  active = false,
  id,
  searchable = false,
  searchPlaceholder,
  noResultsLabel,
  onSearchChange,
  wrapOptions = false,
  menuMaxWidth,
}: Readonly<Props>) {
  // `size` wins when supplied; otherwise fall back to the legacy `dense`
  // boolean so every existing caller keeps behaving identically.
  const resolvedSize = size ?? (dense ? 'dense' : 'default');
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  // Populated via a ref callback on each option button below — used only by
  // the roving-focus effect for the non-searchable case (see its own doc).
  const optionButtonRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const [pos, setPos] = useState<PositionResult>({
    top: 0,
    left: 0,
    placement: 'bottom',
  });
  const [anchorWidth, setAnchorWidth] = useState<number>(0);
  // Rounds the panel's max-height down to a whole number of option rows so
  // the bottom-most visible row is never sliced mid-line — see `update()`'s
  // measurement below for why this can't just be a fixed Tailwind class.
  const [maxPanelHeight, setMaxPanelHeight] = useState<MaxPanelHeight>(undefined);

  const selectedLabel = options.find((o) => o.value === value)?.label;

  // Latest onSearchChange, read inside the query-reset effect below — kept
  // out of that effect's dependency array on purpose (nexa-1115 review,
  // 2026-09-20): the effect must only re-run when `open` changes, not on
  // every render of an unmemoized inline callback prop, which would defeat
  // the "only reset on close" intent. A ref updated every render (same
  // pattern as MultiSelect.tsx's `onSearchRef`) always calls the CURRENT
  // callback without needing it in the deps or silencing the lint rule.
  const onSearchChangeRef = useRef(onSearchChange);
  onSearchChangeRef.current = onSearchChange;

  // `onSearchChange` means the CALLER owns the list — see the prop's docblock.
  const filteredOptions = useMemo(() => {
    if (!searchable || onSearchChange) return options;
    const q = query.trim().toLowerCase();
    if (q.length === 0) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query, searchable, onSearchChange]);

  // Roving "active" option for the listbox's keyboard interaction contract
  // (nexa-1115 review, 2026-09-20) — `role="listbox"`/`role="option"` alone
  // promised the single-tab-stop, arrow-key-driven interaction WAI-ARIA
  // authoring practices define for a listbox; without an active option
  // tracked here and exposed via `aria-activedescendant`, every option was
  // its own Tab stop and arrow keys did nothing, which read as MORE broken
  // to assistive tech than the plain button list this replaced — the ARIA
  // roles promised behavior the component didn't implement.
  const [activeIndex, setActiveIndex] = useState(0);
  const baseId = useId();
  // The trigger needs a REAL id for `aria-labelledby` on the listbox to
  // reference, whether or not the caller supplied one via the `id` prop.
  const triggerId = id ?? `${baseId}-trigger`;
  const listboxId = `${baseId}-listbox`;
  const activeOption = filteredOptions[activeIndex];
  const activeOptionId = open && activeOption ? `${listboxId}-option-${activeIndex}` : undefined;

  // Read inside the effects below via refs (updated every render, same
  // pattern as `onSearchChangeRef` above) so those effects' dependency
  // arrays stay honest and minimal instead of needing every value they
  // read — an unrelated, previously-reviewed effect in this same file was
  // flagged for silencing `react-hooks/exhaustive-deps` instead of fixing
  // this exact class of dependency problem.
  const filteredOptionsRef = useRef(filteredOptions);
  filteredOptionsRef.current = filteredOptions;
  const valueRef = useRef(value);
  valueRef.current = value;

  // Seed the active option to the current value on an actual close→open
  // TRANSITION only (mirrors `wasOpenRef` below) — not on every render
  // while already open, which would fight the user's own arrow-key moves.
  const wasOpenForActiveRef = useRef(open);
  useEffect(() => {
    const wasOpen = wasOpenForActiveRef.current;
    wasOpenForActiveRef.current = open;
    if (!wasOpen && open) {
      const currentValueIndex = filteredOptionsRef.current.findIndex(
        (o) => o.value === valueRef.current,
      );
      setActiveIndex(currentValueIndex >= 0 ? currentValueIndex : 0);
    }
  }, [open]);

  // Re-clamp whenever the filtered list's SIZE changes while open (typing a
  // query can shrink it below the current active index).
  useEffect(() => {
    if (!open) return;
    setActiveIndex((current) =>
      current < filteredOptions.length ? current : Math.max(0, filteredOptions.length - 1),
    );
  }, [open, filteredOptions.length]);

  // Reset the search query whenever the menu closes — and tell the caller, or a
  // remote-backed menu would reopen still narrowed to a term whose text is gone.
  // Gated on an actual open→close TRANSITION (nexa-1115 review, 2026-09-20)
  // — `open` starts `false`, so a plain `if (!open)` also fired on the very
  // first render, sending every `onSearchChange` caller an unrequested
  // `('')` call before the user ever opened the dropdown (costly for a
  // remote-search caller instantiated per-row, e.g. in a table).
  // `wasOpenRef` tracks the PREVIOUS render's `open` so the reset only runs
  // when it was actually open a moment ago.
  const wasOpenRef = useRef(open);
  useEffect(() => {
    const wasOpen = wasOpenRef.current;
    wasOpenRef.current = open;
    if (wasOpen && !open) {
      setQuery('');
      onSearchChangeRef.current?.('');
    }
  }, [open]);

  // Autofocus the search box when opening a searchable menu.
  useEffect(() => {
    if (open && searchable) searchInputRef.current?.focus();
  }, [open, searchable]);

  // Roving-focus for the NON-searchable case (nexa-1115 review, 2026-09-20)
  // — there's no text input to keep focus in here, so real DOM focus moves
  // onto the active option itself (the classic "roving tabIndex" listbox
  // pattern) rather than staying on the trigger button, which — unlike the
  // search input's implicit `textbox` role — doesn't support
  // `aria-activedescendant` per the ARIA spec (a plain `<button>`'s role
  // doesn't list it; the searchable path uses that mechanism on the input
  // instead, since it DOES support it). Re-runs on every `activeIndex`
  // change too, so arrow-key moves keep following focus along.
  useEffect(() => {
    if (open && !searchable) optionButtonRefs.current[activeIndex]?.focus();
  }, [open, searchable, activeIndex]);

  // Position the portaled panel on open + on scroll / resize. Recomputes when
  // `query` changes because filtering alters the panel height.
  useLayoutEffect(() => {
    if (!open) return;
    const anchor = triggerRef.current;
    const panel = panelRef.current;
    if (!anchor || !panel) return;

    const update = (): void => {
      const anchorRect = anchor.getBoundingClientRect();
      setAnchorWidth(anchorRect.width);
      const layout = computeSelectPanelLayout(anchorRect, panel);
      setPos(layout.pos);
      setMaxPanelHeight(layout.maxPanelHeight);
    };

    // The capture-phase listener below closes the dropdown on an ANCESTOR's
    // scroll — e.g. the page, or a scrollable Modal body. It used to call
    // `update()` and re-follow the trigger instead, but the portaled panel
    // is `position: fixed`, so on a real scroll gesture it visibly lagged a
    // frame or two behind the trigger's new position on every scroll tick —
    // reading as the dropdown "dancing"/jittering rather than tracking
    // smoothly (user follow-up, 2026-09-03). Closing outright is the
    // standard floating-menu pattern (matches most native/OS pickers) and
    // has no lag to notice. Capture listeners on `window` run on the way
    // DOWN to the event's target, so a scroll dispatched ON the panel
    // itself still passes through `window` first — ignore any scroll whose
    // target is inside the panel, since the option list's OWN internal
    // scrolling must not close the menu the user is actively scrolling.
    const handleScroll = (e: Event): void => {
      if (panel.contains(e.target as Node)) return;
      setOpen(false);
    };

    update();
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', update);
    };
  }, [open, query]);

  // Outside-click close. The panel is portaled outside the wrapper, so a click
  // on an option would otherwise register as "outside" and close on mousedown
  // BEFORE the option's onClick fires — silently breaking selection. Check both
  // the wrapper and the portaled panel (mirrors Popover's anchor/content check).
  //
  // Listener runs in the CAPTURE phase (third arg `true`). Reason: this Select
  // is routinely rendered inside a Modal or a SlideOverlay, and BOTH of those
  // call `e.stopPropagation()` on their own panel's `onMouseDown` (a common
  // pattern for "click outside the panel closes the overlay" — see
  // `Modal.tsx`/`SlideOverlay.tsx`). React's synthetic stopPropagation also
  // stops the underlying native event from bubbling any further, so a
  // bubble-phase listener on `document` never sees a mousedown that lands
  // anywhere inside one of those panels — the dropdown would stay open
  // forever for any outside click made INSIDE the same dialog/drawer (Asana
  // 1217855754092017). Capture fires on the way DOWN, before any element on
  // the way up gets a chance to stop propagation, so the dropdown still sees
  // every outside click regardless of what a descendant does with the event.
  // Mirrors `components/common/Select.tsx`'s identical fix.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent): void => {
      const target = e.target as Node | null;
      if (
        target &&
        !wrapperRef.current?.contains(target) &&
        !panelRef.current?.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler, true);
    return () => document.removeEventListener('mousedown', handler, true);
  }, [open]);

  const handleSelect = (val: string): void => {
    onChange(val);
    setOpen(false);
    // The clicked/activated option (in the non-searchable roving-focus
    // case, or transiently on click in most browsers either way) is about
    // to unmount with the closing panel — without this, focus would
    // otherwise drop to `document.body`.
    triggerRef.current?.focus();
  };

  const moveActiveIndex = (delta: number): void => {
    if (filteredOptions.length === 0) return;
    setActiveIndex((current) => {
      const next = current + delta;
      if (next < 0) return filteredOptions.length - 1;
      if (next >= filteredOptions.length) return 0;
      return next;
    });
  };

  // Shared Up/Down/Enter/Escape handling for the trigger AND the search
  // input; `allowHomeEnd` is false on the search input DELIBERATELY — Home/
  // End there keep their native text-cursor meaning (matches the WAI-ARIA
  // "Combobox with List Autocomplete" example), while the trigger (a plain
  // button with no text-editing concern) gets the full contract.
  const handleOptionsKeyDown = (event: React.KeyboardEvent, allowHomeEnd: boolean): void => {
    if (!open) return;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        moveActiveIndex(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        moveActiveIndex(-1);
        break;
      case 'Home':
        if (!allowHomeEnd || filteredOptions.length === 0) break;
        event.preventDefault();
        setActiveIndex(0);
        break;
      case 'End':
        if (!allowHomeEnd || filteredOptions.length === 0) break;
        event.preventDefault();
        setActiveIndex(filteredOptions.length - 1);
        break;
      case 'Enter':
        event.preventDefault();
        if (activeOption) handleSelect(activeOption.value);
        break;
      case 'Escape':
        event.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
        break;
      default:
        break;
    }
  };

  // `inset` already sets its own `bg-transparent` below — skipping the chrome
  // class here avoids two bg-* utilities fighting over Tailwind's generated
  // stylesheet order (see the comment on TRIGGER_CHROME_DEFAULT/ACTIVE above).
  // Extracted out of the className array below (rather than left as a nested
  // ternary) per Sonar's "ternary operators should not be nested" rule.
  let triggerChromeClass = TRIGGER_CHROME_DEFAULT;
  if (inset) {
    triggerChromeClass = '';
  } else if (active) {
    triggerChromeClass = TRIGGER_CHROME_ACTIVE;
  }

  return (
    <div
      ref={wrapperRef}
      className={selectRootClassName(inset, className)}
      style={style}>
      <button
        ref={triggerRef}
        id={triggerId}
        type="button"
        className={selectTriggerClassName(resolvedSize, triggerChromeClass, open, inset, disabled)}
        onClick={() => !disabled && setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-label={ariaLabel}
        disabled={disabled}
      >
        <span
          className={
            selectedLabel
              ? 'flex-1 text-left text-[var(--color-text-primary)] whitespace-nowrap overflow-hidden text-ellipsis'
              : 'flex-1 text-left text-[var(--color-text-muted)] whitespace-nowrap overflow-hidden text-ellipsis'
          }
          title={selectedLabel}
        >
          {selectedLabel ?? placeholder}
        </span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          aria-hidden="true"
          className={selectChevronClassName(open)}
        >
          <path
            d="M3 4.5 6 7.5 9 4.5"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            data-testid="select-dropdown"
            /*
              `menuMaxWidth` is what turns the default panel back into a
              grow-to-content one. Without it a `default`/`compact` panel is
              pinned to the trigger's width (see DROPDOWN_WIDTH_BY_SIZE), so a
              raised CAP would have nothing to cap — the width is already
              decided. Passing it opts that one caller back into `w-max`
              floored at the trigger, which is the only shape a max-width
              means anything in.
            */
            className={[
              resolvedSize === 'dense' ? DROPDOWN_CLASS_DENSE : DROPDOWN_CLASS,
              menuMaxWidth !== undefined && resolvedSize !== 'dense' ? 'w-max' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            /*
              `maxWidth` INLINE, so it beats the `max-w-[…]` utility on the
              class above — a second `max-w-` class would tie on specificity and
              be resolved by Tailwind's emission order, not by the caller's
              intent.
            */
            style={{
              top: pos.top,
              left: pos.left,
              /* Keyed by every size, not a dense-vs-rest ternary: a fourth size
                 would otherwise inherit whichever branch it fell into silently.
                 The split follows the TRIGGER_* classes above, not the visual
                 weight — `default` and `compact` are both `w-full`, so a panel
                 wider than the trigger overhangs a control the user sees as
                 full-width (the bug this fixed). `dense` is the only size whose
                 trigger is an inline pill sized to its own label, so it alone
                 keeps `minWidth` and may grow to its option text; see
                 DROPDOWN_CLASS_DENSE. */
              ...(menuMaxWidth === undefined
                ? DROPDOWN_WIDTH_BY_SIZE[resolvedSize](anchorWidth)
                : { minWidth: anchorWidth || undefined }),
              maxHeight: maxPanelHeight,
              maxWidth: menuMaxWidth,
            }}
          >
            {searchable && (
              <div className={SEARCH_WRAP_CLASS} data-select-search="">
                <div className={SEARCH_FIELD_CLASS}>
                  <input
                    ref={searchInputRef}
                    type="text"
                    className={SEARCH_INPUT_CLASS}
                    placeholder={searchPlaceholder}
                    value={query}
                    onChange={(e) => {
                      setQuery(e.target.value);
                      onSearchChange?.(e.target.value);
                    }}
                    onKeyDown={(event) => handleOptionsKeyDown(event, false)}
                    aria-label={searchPlaceholder}
                    role="combobox"
                    aria-expanded={open}
                    aria-autocomplete="list"
                    aria-controls={listboxId}
                    aria-activedescendant={activeOptionId}
                  />
                  {/*
                    Back to the FULL option list in one click. Without it the
                    only way out of a typed filter was backspace or closing the
                    menu — and closing it resets the query anyway, so the reader
                    who wanted the whole list had to throw away the open menu to
                    get it. Focus stays in the box (see `InputClearButton`), so
                    the next term can be typed straight away.
                  */}
                  {query.length > 0 && (
                    <InputClearButton
                      onClear={() => {
                        setQuery('');
                        // A caller serving REMOTE results for the last term
                        // would otherwise keep the menu narrowed to a query
                        // that is no longer on screen.
                        onSearchChange?.('');
                      }}
                    />
                  )}
                </div>
              </div>
            )}

            <div // NOSONAR
              id={listboxId}
              role="listbox"
              aria-labelledby={triggerId}
              className={
                resolvedSize === 'dense'
                  ? OPTIONS_PAD_CLASS_DENSE
                  : OPTIONS_PAD_CLASS
              }
            >
            {/* Gated on `searchable` (nexa-1115 review, 2026-09-18) — this
                empty-state row is for a SEARCH that matched nothing. A
                non-searchable Select's `filteredOptions` is `options`
                verbatim, so gating on emptiness alone rendered a blank,
                unlabeled row for any non-searchable caller whose option
                list happened to be empty (none of which pass
                `noResultsLabel`, since it never applied to them) — a
                regression from the universal "No results" text this prop
                replaced. An empty, non-searchable dropdown now renders
                nothing, same as before that change. */}
            {searchable && filteredOptions.length === 0 ? (
              // `role="option"`/`aria-disabled` (nexa-1115 review,
              // 2026-09-20) — a plain, roleless div was the listbox's only
              // child in this state, which isn't a valid ARIA child (only
              // `option`/`group` are); screen readers could announce it
              // incorrectly or not at all. A disabled, non-selectable
              // option row is the standard pattern for a "no results"
              // placeholder inside a listbox.
              <div // NOSONAR
                role="option"
                aria-disabled="true"
                aria-selected={false}
                className={NO_RESULTS_CLASS}
              >
                {noResultsLabel}
              </div>
            ) : (
              filteredOptions.map((opt, index) => {
                const isSelected = opt.value === value;
                const isActive = index === activeIndex;
                // `isSelected` wins over `isActive` when both apply (the
                // active row also happens to be the current value) — same
                // one-utility-wins-the-background rule as
                // `triggerChromeClass` above; two bg-* classes at once is
                // resolved by Tailwind's emission order, not this array's.
                let optionStateClass = '';
                if (isSelected) {
                  optionStateClass = OPTION_SELECTED;
                } else if (isActive) {
                  optionStateClass = OPTION_ACTIVE;
                }
                // Roving tabIndex (nexa-1115 review, 2026-09-20): only ONE
                // option is ever a real Tab stop. In the `searchable` case
                // it's never this button at all — the search input holds
                // focus and tracks the active option via
                // `aria-activedescendant` (a plain button's implicit role
                // doesn't support that attribute, which is why the
                // non-searchable path moves REAL focus here instead; see
                // the roving-focus effect's own doc above).
                const tabIndexValue = !searchable && isActive ? 0 : -1;
                return (
                  <button // NOSONAR
                    key={opt.value}
                    ref={(el) => {
                      optionButtonRefs.current[index] = el;
                    }}
                    id={`${listboxId}-option-${index}`}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    tabIndex={tabIndexValue}
                    className={[
                      'w-full appearance-none bg-transparent border-0 text-left',
                      OPTION_BASE,
                      wrapOptions ? OPTION_WRAP_CLASS : OPTION_TRUNCATE_CLASS,
                      resolvedSize === 'dense' ? OPTION_DENSE : OPTION_SIZE,
                      optionStateClass,
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    title={opt.label}
                    onClick={() => handleSelect(opt.value)}
                    onKeyDown={(event) => handleOptionsKeyDown(event, true)}
                  >
                    {opt.label}
                  </button>
                );
              })
            )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
